/**
 * ScrollWizardPage - One-page scroll wizard for Americano tournament
 * 7 sections: Config, Players, Courts, Recap, Schedule, Results, Ranking
 */
import React, { useState, useRef, useEffect } from 'react';
import { useTournament } from '../contexts/TournamentContext';
import { useAuth } from '../contexts/AuthContext';
import firebaseSyncService from '../services/firebaseSync';
import ConfigSection from './sections/ConfigSection';
import PlayersSection from './sections/PlayersSection';
import CourtsSection from './sections/CourtsSection';
import RecapSection from './sections/RecapSection';
import ScheduleSection from './sections/ScheduleSection';
import ResultsSection from './sections/ResultsSection';
import RankingSection from './sections/RankingSection';
import LeftNavMenu from './components/LeftNavMenu';
import SettingsSection from './sections/SettingsSection';
import './ScrollWizard.css';

const ScrollWizardPage = ({
  onTournamentsClick,
  onResetClick,
  onSignOutClick,
  onQRCodeClick,
  isSyncing,
  lastSyncTime
}) => {
  const { state } = useTournament();
  const { scheduleGenerated } = state;
  const { currentUser } = useAuth();

  const [currentSection, setCurrentSection] = useState(0);
  const [scrollSnapEnabled, setScrollSnapEnabled] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const sectionRefs = useRef([]);
  const scrollContainerRef = useRef(null);

  const sections = [
    { id: 'config',   label: 'Configuration', component: ConfigSection,   enabled: true },
    { id: 'players',  label: 'Joueurs',        component: PlayersSection,  enabled: true },
    { id: 'courts',   label: 'Terrains',       component: CourtsSection,   enabled: true },
    { id: 'recap',    label: 'Récapitulatif',  component: RecapSection,    enabled: true },
    { id: 'schedule', label: 'Planning',       component: ScheduleSection, enabled: scheduleGenerated },
    { id: 'results',  label: 'Résultats',      component: ResultsSection,  enabled: scheduleGenerated },
    { id: 'ranking',  label: 'Classement',     component: RankingSection,  enabled: scheduleGenerated }
  ];

  // Log 1 — immédiat au montage
  useEffect(() => {
    const id = localStorage.getItem('currentTournamentId');
    console.log('[ScrollWizardPage] Montage | ID localStorage:', id || 'aucun');
  }, []);

  // Log 2 — fetch du nom dès que l'utilisateur est authentifié
  useEffect(() => {
    if (!currentUser) return;
    const id = localStorage.getItem('currentTournamentId');
    if (!id) {
      console.log('[ScrollWizardPage] Tournoi | aucun tournoi chargé');
      return;
    }
    firebaseSyncService.getTournamentName(id, currentUser.email).then((name) => {
      console.log('[ScrollWizardPage] Tournoi | ID:', id, '| Nom:', name || 'inconnu');
    });
  }, [currentUser]);

  // Intersection Observer to detect current section
  useEffect(() => {
    if (!scrollContainerRef.current) return;

    const isMobile = window.innerWidth < 768;
    const rootMargin = isMobile ? '-20% 0px -20% 0px' : '-50% 0px -50% 0px';

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const index = sectionRefs.current.indexOf(entry.target);
          if (index !== -1) setCurrentSection(index);
        }
      });
    }, { root: scrollContainerRef.current, rootMargin, threshold: 0 });

    sectionRefs.current.forEach((ref) => { if (ref) observer.observe(ref); });

    return () => observer.disconnect();
  }, [scheduleGenerated]);

  // Scroll to section
  const scrollToSection = (index) => {
    if (sectionRefs.current[index]) {
      sectionRefs.current[index].scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // After schedule generation, scroll to Schedule section (index 4)
  const handleGenerateTournament = () => {
    setTimeout(() => scrollToSection(4), 500);
  };

  // Enable scroll-snap on first scroll
  useEffect(() => {
    const handleScroll = () => {
      if (!scrollSnapEnabled && scrollContainerRef.current) {
        if (scrollContainerRef.current.scrollTop > 10) {
          setScrollSnapEnabled(true);
        }
      }
    };
    const container = scrollContainerRef.current;
    if (container) container.addEventListener('scroll', handleScroll);
    return () => { if (container) container.removeEventListener('scroll', handleScroll); };
  }, [scrollSnapEnabled]);

  const sectionBackgrounds = {
    0: 'bg-section-config',
    1: 'bg-section-players',
    2: 'bg-section-courts',
    3: 'bg-section-config',
    4: 'bg-section-schedule',
    5: 'bg-section-results',
    6: 'bg-section-ranking'
  };
  const bgClass = sectionBackgrounds[currentSection] || 'bg-section-config';

  return (
    <div
      ref={scrollContainerRef}
      className={`scroll-wizard ${scrollSnapEnabled ? '' : 'scroll-snap-disabled'} ${bgClass}`}
    >
      <LeftNavMenu
        sections={sections}
        currentSection={currentSection}
        onNavigate={scrollToSection}
        onSettingsClick={() => setIsSettingsOpen(true)}
      />

      <SettingsSection
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        onTournamentsClick={onTournamentsClick}
        onResetClick={onResetClick}
        onSignOutClick={onSignOutClick}
        onQRCodeClick={onQRCodeClick}
        isSyncing={isSyncing}
        lastSyncTime={lastSyncTime}
      />

      <div className="scroll-wizard-container">
        {sections.filter(section => section.enabled).map((section, index) => {
          const SectionComponent = section.component;
          const isHidden = !scrollSnapEnabled && index > 0;

          return (
            <section
              key={section.id}
              ref={(el) => (sectionRefs.current[index] = el)}
              className={`scroll-section ${currentSection === index ? 'active' : ''} ${isHidden ? 'hidden-section' : ''}`}
              id={`section-${section.id}`}
            >
              <div className="section-content">
                <SectionComponent
                  onGenerate={section.id === 'recap' ? handleGenerateTournament : undefined}
                />
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
};

export default ScrollWizardPage;
